import { tr } from "./localization";
import { useEffect, useRef, useState } from "react";
import {
  Plus,
  UserRound,
  X,
  Paperclip,
  CalendarDays,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "./api";
import type { Task } from "./types";
import { dayInAlmaty, priorities } from "./utils";
import { useApp, err, userName, Button, Modal, PriorityIcon } from "./shared";

export function CreateTask({
  open,
  close,
  config,
}: {
  open: boolean;
  close: () => void;
  config: { prefill?: string; boardId?: string; columnId?: string };
}) {
  const { me, users, boards, refresh, openTask } = useApp();
  const [title, setTitle] = useState(""),
    [board, setBoard] = useState(""),
    [assignee, setAssignee] = useState(me.id),
    [due, setDue] = useState(""),
    [dueTime, setDueTime] = useState(""),
    [priority, setPriority] = useState("normal"),
    [description, setDescription] = useState(""),
    [review, setReview] = useState(true),
    [checklist, setChecklist] = useState<string[]>([]),
    [files, setFiles] = useState<File[]>([]),
    [busy, setBusy] = useState(false);
  const manual = useRef({ assignee: false, due: false, priority: false });
  const createdTask = useRef<Task | null>(null);
  const uploadedCount = useRef(0);
  const [partial, setPartial] = useState(false);
  const addFiles = (incoming: File[]) => {
    if (busy) return;
    if (files.length + incoming.length + uploadedCount.current > 10) {
      toast.error(tr("Не более 10 файлов на задачу"));
      return;
    }
    if (incoming.some((file) => file.size > 20 * 1024 * 1024)) {
      toast.error(tr("Файл больше 20 МБ"));
      return;
    }
    setFiles((old) => [...old, ...incoming]);
  };
  const closeForm = () => {
    if (busy) return;
    close();
    if (createdTask.current) openTask(createdTask.current.id);
  };
  const writable = boards.filter(
    (b) => ["owner", "member"].includes(b.role) && !b.is_archived,
  );
  useEffect(() => {
    if (open) {
      setTitle(config.prefill || "");
      manual.current = { assignee: false, due: false, priority: false };
      createdTask.current = null;
      uploadedCount.current = 0;
      setPartial(false);
      setAssignee(me.id);
      setDue("");
      setPriority("normal");
      const selectedBoard =
        boards.find((b) => b.id === config.boardId) || writable[0];
      setBoard(selectedBoard?.id || "");
      setDescription("");
      setDueTime("");
      setFiles([]);
      setChecklist([]);
      setReview(selectedBoard?.default_review ?? true);
    }
  }, [open, config.prefill, config.boardId]);
  const [preview, setPreview] = useState<{
    assignee_id?: string;
    due_date?: string;
    priority?: string;
  }>({});
  useEffect(() => {
    if (!open || !title.trim()) {
      setPreview({});
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      api<typeof preview>("/tasks/parse", "POST", { text: title })
        .then((parsed) => {
          if (active) {
            setPreview(parsed);
            if (!manual.current.assignee)
              setAssignee(parsed.assignee_id || me.id);
            if (!manual.current.priority)
              setPriority(parsed.priority || "normal");
            if (!manual.current.due) setDue(parsed.due_date || "");
          }
        })
        .catch(() => {});
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [title, open]);
  const today = new Date(dayInAlmaty() + "T12:00:00Z");
  const shortcuts = [
    [0, "Сегодня"],
    [1, "Завтра"],
    ["fri", "В пятницу"],
    [7, "Через неделю"],
    ["none", "Без срока"],
  ].map(([days, label]) => {
    const date = new Date(today);
    date.setUTCDate(
      date.getUTCDate() +
        (days === "fri" ? (5 - date.getUTCDay() + 7) % 7 : Number(days) || 0),
    );
    return {
      label: String(label),
      value: days === "none" ? "" : date.toISOString().slice(0, 10),
    };
  });
  return (
    <Modal
      open={open}
      onClose={closeForm}
      title={tr("Новая задача")}
      className="create-dialog"
    >
      <form
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          addFiles(Array.from(e.dataTransfer.files));
        }}
        onPaste={(e) => {
          if (e.clipboardData.files.length) {
            e.preventDefault();
            addFiles(Array.from(e.clipboardData.files));
          }
        }}
        onSubmit={async (e) => {
          e.preventDefault();
          if (busy) return;
          setBusy(true);
          try {
            if (!createdTask.current) {
              const parsed = await api<{
                title: string;
                assignee_id?: string;
                due_date?: string;
                priority?: string;
              }>("/tasks/parse", "POST", { text: title });
              const res = await api<{ task: Task }>("/tasks", "POST", {
                board_id: board,
                column_id:
                  board === config.boardId
                    ? config.columnId || undefined
                    : undefined,
                title: parsed.title,
                description,
                checklist: checklist.map((text) => text.trim()).filter(Boolean),
                assignee_id:
                  (manual.current.assignee
                    ? assignee
                    : parsed.assignee_id || assignee) || null,
                priority: manual.current.priority
                  ? priority
                  : parsed.priority || priority,
                due_date:
                  (manual.current.due ? due : parsed.due_date || due) || null,
                due_time: dueTime || null,
                review_required: review,
              });
              createdTask.current = res.task;
            }
            const failed: File[] = [];
            for (const file of files) {
              try {
                const fd = new FormData();
                fd.append("file", file);
                await api(
                  `/tasks/${createdTask.current.id}/attachments`,
                  "POST",
                  fd,
                );
                uploadedCount.current += 1;
              } catch (e) {
                failed.push(file);
                err(e);
              }
            }
            setFiles(failed);
            refresh();
            if (failed.length) {
              setPartial(true);
              return;
            }
            close();
            openTask(createdTask.current.id);
            toast.success(tr("Задача создана"));
          } catch (e) {
            err(e);
          } finally {
            setBusy(false);
          }
        }}
      >
        {partial && (
          <p role="alert" className="upload-partial">
            {tr(
              "Задача создана. Не все файлы загружены — повторите загрузку оставшихся файлов.",
            )}
          </p>
        )}
        <label className="sr-only" htmlFor="create-title">
          {tr("Название")}
        </label>
        <input
          id="create-title"
          disabled={busy || partial}
          className="create-title"
          autoFocus
          aria-label={tr("Название")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={300}
          required
          placeholder={tr("Что нужно сделать?")}
        />
        <div className="parsed-preview" aria-live="polite">
          {preview.assignee_id && (
            <span className="chip">
              <UserRound size={12} />
              {userName(users, assignee || null)}
            </span>
          )}
          {preview.due_date && (
            <span className="chip">
              <CalendarDays size={12} />
              {due || tr("Без срока")}
            </span>
          )}
          {preview.priority && (
            <span className="chip">
              <PriorityIcon priority={priority} label />
            </span>
          )}
          {!Object.keys(preview).some((k) => k !== "title") && (
            <small className="muted">
              {tr(
                "Подсказка: можно писать «Заказать болты @Айдар завтра !срочно»",
              )}
            </small>
          )}
        </div>
        <label className="sr-only" htmlFor="create-description">
          {tr("Описание")}
        </label>
        <textarea
          disabled={busy || partial}
          id="create-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={20000}
          rows={3}
          placeholder={tr(
            "Описание — что важно знать исполнителю (необязательно)",
          )}
        />
        <section className="create-checklist" aria-label={tr("Чек-лист")}>
          <b>{tr("Чек-лист")}</b>
          {checklist.map((text, index) => (
            <div className="create-checklist-row" key={index}>
              <span aria-hidden="true">{index + 1}.</span>
              <input
                aria-label={tr("Пункт чек-листа") + " " + (index + 1)}
                placeholder={tr("Что нужно сделать?")}
                value={text}
                maxLength={500}
                disabled={busy || partial}
                autoFocus
                onChange={(e) =>
                  setChecklist((items) =>
                    items.map((item, i) =>
                      i === index ? e.target.value : item,
                    ),
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (text.trim() && checklist.length < 100)
                      setChecklist((items) => [...items, ""]);
                  }
                }}
              />
              <Button
                className="icon small"
                aria-label={tr("Удалить пункт") + " " + (index + 1)}
                disabled={busy || partial}
                onClick={() =>
                  setChecklist((items) => items.filter((_, i) => i !== index))
                }
              >
                <X size={16} />
              </Button>
            </div>
          ))}
          <Button
            disabled={busy || partial || checklist.length >= 100}
            onClick={() => setChecklist((items) => [...items, ""])}
          >
            <Plus size={16} />
            {tr("Добавить пункт")}
          </Button>
        </section>
        <label className="create-files">
          <span className="file-drop-zone">
            <Upload size={20} aria-hidden="true" />
            <span>
              {tr("Перетащите файлы сюда, вставьте из буфера")} (
              <kbd>Ctrl V</kbd>) {tr("или")} <u>{tr("выберите")}</u>
              <small>
                {tr("Фото, PDF, Word, Excel — до 20 МБ, не больше 10 файлов")}
              </small>
            </span>
            <input
              type="file"
              aria-label={tr("Выбрать файлы")}
              multiple
              accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.txt,.csv,.docx,.xlsx,.pptx"
              disabled={busy}
              onChange={(e) => {
                addFiles(Array.from(e.target.files || []));
                e.target.value = "";
              }}
            />
          </span>
        </label>
        {files.map((f, i) => (
          <div className="pending-file" key={f.name + i}>
            <Paperclip size={14} />
            <span>{f.name}</span>
            <small>{Math.ceil(f.size / 1024)} KB</small>
            <Button
              className="icon small"
              aria-label={tr("Удалить файл ") + f.name}
              disabled={busy}
              onClick={() =>
                setFiles((old) => old.filter((item) => item !== f))
              }
            >
              <X size={14} />
            </Button>
          </div>
        ))}
        <div className="form-grid">
          <label>
            {tr("Исполнитель")}
            <select
              disabled={busy || partial}
              value={assignee}
              onChange={(e) => {
                manual.current.assignee = true;
                setAssignee(e.target.value);
              }}
            >
              <option value="">{tr("Не назначен")}</option>
              {users
                .filter((u) => u.is_active !== false)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                    {u.id === me.id ? " (" + tr("я") + ")" : ""}
                  </option>
                ))}
            </select>
          </label>
          <label>
            {tr("Приоритет")}
            <select
              disabled={busy || partial}
              value={priority}
              onChange={(e) => {
                manual.current.priority = true;
                setPriority(e.target.value);
              }}
            >
              {Object.entries(priorities).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="create-due">
          <label htmlFor="create-due">{tr("Срок")}</label>
          <div className="date-shortcuts">
            {shortcuts.map(({ label, value }) => (
              <button
                type="button"
                key={label}
                aria-pressed={due === value}
                disabled={busy || partial}
                onClick={() => {
                  manual.current.due = true;
                  setDue(value);
                  if (!value) setDueTime("");
                }}
              >
                {tr(label)}
              </button>
            ))}
            <input
              id="create-due"
              disabled={busy || partial}
              type="date"
              value={due}
              onChange={(e) => {
                manual.current.due = true;
                setDue(e.target.value);
                if (!e.target.value) setDueTime("");
              }}
              aria-label={tr("Выбрать дату")}
            />
          </div>
          {due && (
            <div className="create-time-picker">
              <label className="create-time-toggle">
                <input
                  type="checkbox"
                  checked={Boolean(dueTime)}
                  disabled={busy || partial}
                  onChange={(e) => setDueTime(e.target.checked ? "09:00" : "")}
                />
                {tr("Указать время")}
              </label>
              {dueTime ? (
                <div className="create-time-fields">
                  <label>
                    {tr("Часы")}
                    <select
                      aria-label={tr("Часы")}
                      value={dueTime.slice(0, 2)}
                      disabled={busy || partial}
                      onChange={(e) =>
                        setDueTime(e.target.value + dueTime.slice(2))
                      }
                    >
                      {Array.from({ length: 24 }, (_, i) =>
                        String(i).padStart(2, "0"),
                      ).map((v) => (
                        <option key={v}>{v}</option>
                      ))}
                    </select>
                  </label>
                  <span aria-hidden="true">:</span>
                  <label>
                    {tr("Минуты")}
                    <select
                      aria-label={tr("Минуты")}
                      value={dueTime.slice(3, 5)}
                      disabled={busy || partial}
                      onChange={(e) =>
                        setDueTime(dueTime.slice(0, 3) + e.target.value)
                      }
                    >
                      {Array.from({ length: 60 }, (_, i) =>
                        String(i).padStart(2, "0"),
                      ).map((v) => (
                        <option key={v}>{v}</option>
                      ))}
                    </select>
                  </label>
                  <small className="muted">{tr("По времени Алматы")}</small>
                </div>
              ) : (
                <small className="muted">
                  {tr("Без точного времени — до конца дня")}
                </small>
              )}
            </div>
          )}
        </div>
        {assignee !== me.id && (
          <div className="create-review">
            <label className="switch">
              <input
                type="checkbox"
                disabled={busy || partial}
                checked={review}
                onChange={(e) => setReview(e.target.checked)}
                aria-label={tr("Проверить результат перед закрытием")}
              />
              <span />
            </label>
            <div>
              <b>{tr("Проверить результат перед закрытием")}</b>
              <p className="muted">
                {tr(
                  "Исполнитель нажмёт «Сдать на проверку», а вы примете или вернёте. Выключите для простых задач — исполнитель просто нажмёт «Выполнено»",
                )}
              </p>
            </div>
          </div>
        )}
        <label>
          {tr("Доска")}
          <select
            disabled={busy || partial}
            value={board}
            onChange={(e) => {
              setBoard(e.target.value);
              setReview(
                boards.find((b) => b.id === e.target.value)?.default_review ??
                  true,
              );
            }}
            required
          >
            {writable.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <footer>
          <small className="create-shortcut-hint">
            <kbd>Enter</kbd> {tr("создать")} · <kbd>Esc</kbd> {tr("закрыть")}
          </small>
          <Button onClick={closeForm} disabled={busy}>
            {partial ? tr("Открыть задачу") : tr("Отмена")}
          </Button>
          <Button
            type="submit"
            className="primary"
            disabled={busy || !board || !title.trim() || !navigator.onLine}
          >
            <Plus size={16} aria-hidden="true" />
            {busy
              ? tr("Сохраняю…")
              : partial
                ? tr("Повторить загрузку")
                : tr("Создать задачу")}
          </Button>
        </footer>
      </form>
    </Modal>
  );
}
